import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import { ImageUpload } from "../../components/ImageUpload";
import { restaurantMapsUrl, isSafeGoogleMapsUrl } from "../../lib/maps";
import { toast } from "sonner";
import { Volume2 } from "lucide-react";
import { playNewOrderBell, unlockAudio } from "../../lib/kitchenAudio";

const DAYS = [["monday", "Ponedeljak"], ["tuesday", "Utorak"], ["wednesday", "Sreda"], ["thursday", "Četvrtak"], ["friday", "Petak"], ["saturday", "Subota"], ["sunday", "Nedelja"]];
const inputCls = "w-full px-3 py-2 rounded-lg border-2 border-slate-200 outline-none focus:ring-2 focus:ring-brand-red";
const defaultHours = () => Object.fromEntries(DAYS.map(([k]) => [k, { open: "10:00", close: "23:00", closed: false }]));
function Field({ label, children }) { return <label className="block mb-3"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span><div className="mt-1">{children}</div></label>; }

export default function RestaurantSettings() {
  const { restaurant, role, reload } = useOutletContext();
  const isAdmin = role === "PLATFORM_ADMIN";
  const [f, setF] = useState(() => ({ ...restaurant, opening_hours: restaurant.opening_hours || defaultHours() }));
  const [geoBusy, setGeoBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setHour = (d, k, v) => setF((p) => ({ ...p, opening_hours: { ...p.opening_hours, [d]: { ...p.opening_hours[d], [k]: v } } }));
  const geocode = async () => {
    if (!f.address) { toast.error("Unesite adresu"); return; }
    setGeoBusy(true);
    try {
      const { data } = await api.post("/admin/restaurants/geocode", { address: f.address });
      setF((p) => ({ ...p, latitude: data.latitude, longitude: data.longitude }));
      toast.success("Koordinate pronađene");
    } catch (e) { toast.error(e?.response?.data?.detail || "Adresu nije moguće locirati"); }
    setGeoBusy(false);
  };
  const save = async () => {
    if (!f.name) { toast.error("Naziv je obavezan"); return; }
    try {
      await api.put(`/admin/restaurants/${restaurant.id}`, {
        name: f.name, category: f.category, description: f.description, address: f.address, phone: f.phone,
        latitude: f.latitude === "" || f.latitude == null ? null : Number(f.latitude),
        longitude: f.longitude === "" || f.longitude == null ? null : Number(f.longitude),
        google_maps_url: (f.google_maps_url || "").trim(),
        google_place_id: (f.google_place_id || "").trim(),
        cover_image: f.cover_image, logo: f.logo, opening_hours: f.opening_hours,
        normal_pickup_range: f.normal_pickup_range || "15_20",
        payment_method: f.payment_method || "BOTH",
        vista_payment_method: f.vista_payment_method || f.payment_method || "BOTH",
        is_open: f.is_open, temp_closed: f.temp_closed, accepting_orders: f.accepting_orders,
        is_hiring: !!f.is_hiring,
      });      toast.success("Sačuvano"); reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Greška pri čuvanju"); }
  };
  return (
    <div className="max-w-2xl">
      <h1 className="font-display font-black text-2xl mb-5">Restoran</h1>
      <div className="bg-white rounded-2xl border p-6">
        <Field label="Naziv"><input data-testid="settings-name" className={inputCls} value={f.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Kuhinja / kategorija"><input className={inputCls} value={f.category || ""} onChange={(e) => set("category", e.target.value)} /></Field>
        <Field label="Opis"><textarea className={inputCls} value={f.description || ""} onChange={(e) => set("description", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Adresa"><input className={inputCls} value={f.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="Broj telefona restorana (vidljiv kupcima)"><input data-testid="settings-phone" className={inputCls} value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+381 18 123 456" /></Field>
        </div>
        <div className="rounded-xl border-2 border-slate-100 bg-slate-50 p-3 mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Lokacija (za "Najbliže tebi")</span>
            <button data-testid="geocode-btn" type="button" onClick={geocode} disabled={geoBusy}
              className="text-xs font-bold bg-brand-red hover:bg-brand-redHover text-white rounded-full px-3 py-1.5 disabled:opacity-60">
              {geoBusy ? "Tražim..." : "Pronađi koordinate iz adrese"}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude"><input data-testid="settings-lat" type="number" step="any" className={inputCls} value={f.latitude ?? ""} onChange={(e) => set("latitude", e.target.value)} placeholder="43.3209" /></Field>
            <Field label="Longitude"><input data-testid="settings-lng" type="number" step="any" className={inputCls} value={f.longitude ?? ""} onChange={(e) => set("longitude", e.target.value)} placeholder="21.8958" /></Field>
          </div>
          <p className="text-[11px] text-slate-400">Klikni "Pronađi koordinate iz adrese" ili unesi ručno. Ako ostaviš prazno, pokušaćemo automatski iz adrese pri čuvanju.</p>
        </div>
        <div className="rounded-xl border-2 border-slate-100 bg-slate-50 p-3 mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Google Maps lokacija</span>
            <button data-testid="open-maps-btn" type="button"
              onClick={() => {
                const url = restaurantMapsUrl(f);
                if (!url) { toast.error("Nema dovoljno podataka za Google Maps (unesi link, naziv ili adresu)."); return; }
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              className="text-xs font-bold bg-slate-800 text-white rounded-full px-3 py-1.5">
              Otvori na Google Maps
            </button>
          </div>
          <Field label="Google Maps link">
            <input data-testid="settings-gmaps-url" className={inputCls} value={f.google_maps_url || ""}
              onChange={(e) => set("google_maps_url", e.target.value)} placeholder="https://maps.app.goo.gl/..." />
          </Field>
          {f.google_maps_url && !isSafeGoogleMapsUrl(f.google_maps_url) && (
            <p data-testid="gmaps-url-warning" className="text-[11px] text-brand-red mb-1">Link mora biti https Google Maps adresa (google.com/maps ili maps.app.goo.gl).</p>
          )}
          <p className="text-[11px] text-slate-400">Otvorite Google Maps → nađite svoj restoran → <b>Deli / Share</b> → <b>Copy link</b> → nalepite ovde, kako bi kupci otvorili tačnu stranicu restorana umesto anonimne tačke na mapi.</p>
        </div>
        <ImageUpload label="Cover slika" value={f.cover_image} onChange={(v) => set("cover_image", v)} />
        <ImageUpload label="Logo" value={f.logo} onChange={(v) => set("logo", v)} />
        <Field label="Prikaz vremena preuzimanja (kupcu)">
          <select data-testid="settings-pickup-range" className={inputCls} value={f.normal_pickup_range || "15_20"} onChange={(e) => set("normal_pickup_range", e.target.value)}>
            <option value="10_15">10–15 min</option>
            <option value="15_20">15–20 min</option>
            <option value="20_30">20–30 min</option>
            <option value="30_PLUS">30+ min</option>
          </select>
          <p className="text-[11px] text-slate-400 mt-1">Ovo vreme možete menjati i direktno na strani Porudžbine (kontrola „Vreme").</p>
        </Field>
        <Field label="Način plaćanja (Ponesi kupci)">
          <select data-testid="settings-payment-method" className={inputCls} value={f.payment_method || "BOTH"} onChange={(e) => set("payment_method", e.target.value)}>
            <option value="CASH">Gotovina</option>
            <option value="CARD">Kartica</option>
            <option value="BOTH">Gotovina i kartica</option>
          </select>
          <p className="text-[11px] text-slate-400 mt-1">Kartica znači plaćanje karticom u restoranu pri preuzimanju (nema onlajn plaćanja).</p>
        </Field>
        <Field label="Vista plaćanje (lokal / šalter)">
          <select data-testid="settings-vista-payment-method" className={inputCls}
            value={f.vista_payment_method || f.payment_method || "BOTH"}
            onChange={(e) => set("vista_payment_method", e.target.value)}>
            <option value="CASH">Samo gotovina</option>
            <option value="CARD">Samo kartica</option>
            <option value="BOTH">Gotovina i kartica</option>
            <option value="NONE">Ne evidentiramo način plaćanja</option>
          </select>
          <p className="text-[11px] text-slate-400 mt-1">
            Odvojeno od Ponesi plaćanja. Ako izaberete „Ne evidentiramo", Vista porudžbina se šalje bez izbora načina plaćanja.
          </p>
        </Field>
        <Field label="Zapošljavamo">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input data-testid="settings-is-hiring" type="checkbox" checked={!!f.is_hiring}
              onChange={(e) => set("is_hiring", e.target.checked)} />
            Prikaži oznaku „ZAPOŠLJAVAMO“ na kartici restorana
          </label>
        </Field>
        <div className="border-t pt-3">
          <h4 className="font-bold text-sm mb-2">Radno vreme</h4>
          {DAYS.map(([k, label]) => (
            <div key={k} className="flex items-center gap-3 mb-2">
              <span className="w-24 text-sm font-semibold">{label}</span>
              <input type="time" className="px-2 py-1.5 rounded-lg border-2 border-slate-200" value={f.opening_hours[k].open} onChange={(e) => setHour(k, "open", e.target.value)} disabled={f.opening_hours[k].closed} />
              <span>–</span>
              <input type="time" className="px-2 py-1.5 rounded-lg border-2 border-slate-200" value={f.opening_hours[k].close} onChange={(e) => setHour(k, "close", e.target.value)} disabled={f.opening_hours[k].closed} />
              <label className="text-xs flex items-center gap-1 ml-1"><input type="checkbox" checked={f.opening_hours[k].closed} onChange={(e) => setHour(k, "closed", e.target.checked)} />Zatvoreno</label>
            </div>
          ))}
        </div>
        <div className="border-t pt-3">
          <h4 className="font-bold text-sm mb-2">Zvučna obaveštenja</h4>
          <p className="text-[11px] text-slate-400 mb-2">
            Zvuk novih porudžbina je uvek uključen i ne može se isključiti. Ovim dugmetom proverite
            da li tablet stvarno pušta zvuk (jačina zvuka i sistemski mute uređaja nisu pod kontrolom aplikacije).
          </p>
          <button data-testid="test-sound-btn" type="button"
            onClick={async () => {
              const ready = await unlockAudio();
              if (ready && playNewOrderBell()) toast.success("Zvuk je aktivan — proverite jačinu na uređaju");
              else toast.error("Zvuk obaveštenja nije aktivan. Dodirnite da ga ponovo uključite.");
            }}
            className="rounded-lg bg-slate-100 px-4 min-h-[48px] text-sm font-bold flex items-center gap-2">
            <Volume2 size={16} />TESTIRAJ ZVUK
          </button>
        </div>
        <button data-testid="save-settings" onClick={save} className="w-full bg-brand-red hover:bg-brand-redHover text-white rounded-lg py-3 font-bold mt-4">Sačuvaj</button>
        {!isAdmin && <p className="text-xs text-slate-400 mt-2">Aktivaciju/deaktivaciju restorana kontroliše Ponesi administrator.</p>}
      </div>
    </div>
  );
}
